import camelCaseKeys from 'camelcase-keys-deep'
import { createApp, h } from 'vue'
import pinia from '@/common/pinia'
import configureApp, { VUE3_ROOT_ID } from '@/common/appConfig'
import { useGlobalStore } from '@/stores/global'
import initSidebar from '@/config/sidebars/init'

import AppLayout from '@/components/layout/AppLayout.vue'

/**
 * Creates and mounts a Vue 3 application.
 * Automatically wraps in AppLayout if layout data is available in global store.
 * Global store is populated by the Global styxie before this runs.
 *
 * @param {Object} component - The page component to render
 * @param {Object} options
 * @param {Object} options.props - Props to pass to the page component
 * @param {Object|null} options.layout - Layout component to wrap page in (default: AppLayout)
 *                                       Pass `null` to render without layout
 * @param {Object} options.layoutProps - Props to pass to the layout component
 * @param {Object} options.sidebar - Sidebar config to use for this page
 * @param {Function} options.state - Optional callback to initialize additional Pinia stores
 * @returns {Object|null} - Vue app instance or null if mount element not found
 *
 * @example
 * // Standard page with default layout
 * createVue3App(MyPage, { props: data })
 *
 * @example
 * // Page with sidebar
 * import formTrackingSidebar from '@/config/sidebars/formTracking'
 * createVue3App(MyPage, { props: data, sidebar: formTrackingSidebar })
 *
 * @example
 * // Fullscreen page without layout
 * createVue3App(FullscreenEditor, { props: data, layout: null })
 *
 * @example
 * // Page with custom layout and layout props
 * createVue3App(MyPage, {
 *   props: data,
 *   layout: DashboardLayout,
 *   layoutProps: { title: 'Overview' }
 * })
 *
 * @example
 * // With additional Pinia store
 * createVue3App(MyPage, {
 *   props: data,
 *   state: (pinia) => {
 *     const store = useMyStore(pinia)
 *     store.initialize(data)
 *   }
 * })
 */
export function createVueApp(component, options = {}) {
  const {
    props = {},
    layout = AppLayout,
    layoutProps = {},
    sidebar,
    state
  } = options

  const mountEl = document.getElementById(VUE3_ROOT_ID)
  if (!mountEl) {
    console.error(`[createVue3App]: Mount element #${VUE3_ROOT_ID} not found`)
    return null
  }

  const globalStore = useGlobalStore(pinia)
  const hasGlobalData = globalStore.user?.email != null

  if (!hasGlobalData) {
    console.warn('[createVue3App]: Global store not initialized')
  }

  // browser cache going hard, persisting js state. So this is actually required to
  // clear state so sidebar is properly updated
  globalStore.sidebar = null

  if (sidebar) {
    initSidebar(globalStore, sidebar)
  }

  let rootComponent
  let rootProps = {}

  if (layout && hasGlobalData) {
    rootComponent = {
      name: 'AppRoot',
      render() {
        return h(layout, layoutProps, {
          default: () => h(component, camelCaseKeys(props))
        })
      }
    }
  } else {
    rootComponent = component
    rootProps = props
  }

  const app = createApp(rootComponent, rootProps)
  app.use(pinia)
  configureApp(app)

  if (state) {
    state(pinia)
  }

  app.mount(mountEl)

  return app
}

export default createVueApp
